// ============================================================================
// TeamOverviewCard — carte equipe onglet Apercu management (GEST CARTES METIER)
// ============================================================================

import { GsAvatar, GsProgressBar, StatusBadge } from '@gs/ui';
import { useTranslation } from '../../i18n';
import type { TeamSummary } from '../../hooks/useTeams';
import {
  BusinessEntityCard,
  BusinessEntityCardMetaItem,
  BusinessEntityCardMetaRow,
} from '../BusinessEntityCard';
import { monthShort } from '../../pages/equipes/dates';

export interface TeamOverviewCardProps {
  team: TeamSummary;
  onOpen: (teamId: string) => void;
}

export function TeamOverviewCard({ team, onOpen }: TeamOverviewCardProps) {
  const { t, i18n } = useTranslation();
  const directorLabel = team.directorName ?? team.directorEmail;

  let pulseContent;
  if (team.submittedThisPeriod && team.currentMood != null) {
    pulseContent = t('gest:equipes.pulse.score', { score: team.currentMood });
  } else if (team.latestMood != null && team.latestPeriod) {
    pulseContent = (
      <span className="font-medium text-slate-400 dark:text-slate-500">
        {t('gest:equipes.pulse.lastSeen', {
          month: monthShort(i18n.language, team.latestPeriod),
          score: team.latestMood,
        })}
      </span>
    );
  } else {
    pulseContent = (
      <span className="font-medium text-slate-400 dark:text-slate-500">
        {t('gest:equipes.pulse.none')}
      </span>
    );
  }

  return (
    <BusinessEntityCard
      title={team.name}
      subtitle={team.department ?? undefined}
      badge={
        team.submittedThisPeriod ? (
          <StatusBadge variant="success">{t('gest:equipes.pulse.submitted')}</StatusBadge>
        ) : (
          <StatusBadge variant="neutral">{t('gest:equipes.pulse.pending')}</StatusBadge>
        )
      }
      activateLabel={t('gest:equipes.cards.openTeam', { name: team.name })}
      onActivate={() => onOpen(team.id)}
    >
      <div className="flex min-h-8 items-center gap-2">
        {directorLabel ? (
          <>
            <GsAvatar name={directorLabel} size="sm" />
            <p className="min-w-0 truncate text-meta text-slate-600 dark:text-slate-300">
              {directorLabel}
            </p>
          </>
        ) : (
          <p className="text-meta italic text-slate-400 dark:text-slate-500">
            {t('gest:equipes.noDirector')}
          </p>
        )}
      </div>
      <BusinessEntityCardMetaRow>
        <BusinessEntityCardMetaItem label={t('gest:equipes.columns.members')}>
          {team.memberCount || team.headcount}
        </BusinessEntityCardMetaItem>
        <BusinessEntityCardMetaItem label={t('gest:equipes.columns.pulse')}>
          {pulseContent}
        </BusinessEntityCardMetaItem>
        <BusinessEntityCardMetaItem label={t('gest:equipes.columns.objectives')} className="min-w-24 flex-1">
          {team.objectivesTotal > 0 ? (
            <GsProgressBar
              value={team.objectivesDone}
              max={team.objectivesTotal}
              label
              className="w-full max-w-32"
            />
          ) : (
            <span className="font-medium text-slate-400 dark:text-slate-500">—</span>
          )}
        </BusinessEntityCardMetaItem>
      </BusinessEntityCardMetaRow>
    </BusinessEntityCard>
  );
}
